import { Box, Button, Paper, Typography } from "@mui/material";
import { useRef, useState } from "react";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import CreateNewFolderIcon from "@entrilia/shared/icons/CreateNewFolderIcon";
import DragAndDropList from "../../../common/DragAndDropList";
import GlobalDndProvider from "../../../common/GlobalDndProvider";
import EditGroupDialog from "./EditGroupDialog";
import LayoutGroupCard from "./LayoutGroupCard";
import ObjectLayoutContext from "./ObjectLayoutContext";
import type { LayoutField, LayoutFieldGroup } from "./objectLayoutState";
import { moveFieldAction, moveGroupAction } from "./objectLayoutState";

interface DialogState {
  openDialog?: "edit_group";
  editedGroupName?: string;
}

type FieldAndGroupName = [LayoutField, string];

const ObjectLayoutEditor = () => {
  const { layoutState, setLayoutState } = useDefinedContext(ObjectLayoutContext);
  const [editGroupDialogState, setEditGroupDialogState] = useState<DialogState>({});

  const draggedFieldRef = useRef<FieldAndGroupName | undefined>(undefined);

  const handleDragField = (groupName: string) => (field: LayoutField) => {
    draggedFieldRef.current = [field, groupName];
  };

  const handleDropField = (groupName: string) => (index: number, field: LayoutField | undefined) => {
    if (field) {
      setLayoutState(moveFieldAction(groupName, groupName, index, field));
      return;
    }

    // When field is moved from a different group, the callback arg will be undefined
    const fieldAndGroupName = draggedFieldRef.current;
    if (fieldAndGroupName) {
      const [field, sourceGroupName] = fieldAndGroupName;
      setLayoutState(moveFieldAction(sourceGroupName, groupName, index, field));
    }
  };

  const handleDropGroup = (index: number, draggingGroup: LayoutFieldGroup | undefined) => {
    if (draggingGroup) {
      setLayoutState(moveGroupAction(index, draggingGroup));
    }
  };

  return (
    <>
      <Paper variant="outlined">
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", py: 2, px: 2.5 }}>
          <Typography variant="h6">Details</Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setEditGroupDialogState({ openDialog: "edit_group" })}
          >
            Add Group
          </Button>
        </Box>
        <Box sx={{ width: "100%", px: 2.5 }}>
          <GlobalDndProvider>
            <DragAndDropList<LayoutFieldGroup>
              type="layout_group"
              items={layoutState.fieldGroups}
              getItemKey={(group) => group.name}
              onDrop={handleDropGroup}
              renderItem={(group) => (
                <LayoutGroupCard
                  key={group.name}
                  group={group}
                  onDragField={handleDragField(group.name)}
                  onDropField={handleDropField(group.name)}
                  onRenameGroup={(groupName) =>
                    setEditGroupDialogState({ openDialog: "edit_group", editedGroupName: groupName })
                  }
                />
              )}
              renderCustomDragItemLayer={(group) => (
                <Paper sx={{ width: "32rem" }}>
                  <LayoutGroupCard group={group} hideFieldsAndActions />
                </Paper>
              )}
              options={{ notDraggable: () => Boolean(layoutState.isAddingFieldToGroup) }}
            />
          </GlobalDndProvider>
        </Box>
      </Paper>
      {editGroupDialogState.openDialog === "edit_group" && (
        <EditGroupDialog
          editedGroupName={editGroupDialogState.editedGroupName}
          onClose={() => setEditGroupDialogState({})}
        />
      )}
    </>
  );
};

export default ObjectLayoutEditor;
